import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  RadioGroup as MuiRadioGroup,
  Radio,
} from '@mui/material';
import { useField, Field } from 'formik';
import { FC, MouseEvent } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { fontSizeValueSchema, hexColorValidationSchema, useAppDispatch } from '../../lib';
import { openSidebar } from '../../store';
import { Field as FormField, FieldType, RadioGroup } from '../../types/form-fields';
import { CheckboxField, ColorPickerField, FormRow, InputField } from '../fields';
import { FormBlock } from './common';

export const isRadioGroupField = (field: FormField): field is RadioGroup => {
  return field.type === FieldType.RadioGroup;
};

export const defaultRadioGroupFieldProps: RadioGroup = {
  type: FieldType.RadioGroup,
  label: 'Radio group label',
  defaultValue: 'First',
  required: true,
  fontWeight: 400,
  labelFontWeight: 400,
  color: '#fff',
  options: 'First, Second',
  row: true,
  fontSize: 16,
  labelFontSize: 16,
};

type RadioGroupFieldSettingsProps = {
  name: string;
  remove?: () => void;
};

const textValueSchema = Yup.string().optional().max(255, 'Should be < 255 characters');
const optionsSchema = Yup.string().optional().max(1023, 'Should be < 255 characters');

const RadioGroupFieldSettings: FC<RadioGroupFieldSettingsProps> = ({ name, remove }) => {
  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  const { t } = useTranslation();

  return (
    <RadioGroupFieldSettingsWrapper>
      <FormRow>
        <Field
          name={`${name}.label`}
          component={InputField}
          label={t('pages.campaign.checkboxField.label')}
          multiline
          maxRows={5}
          validate={(value: string) => validate(value, textValueSchema)}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.defaultValue`}
          component={InputField}
          label={t('pages.campaign.checkboxField.defaultValue')}
          validate={(value: string) => validate(value, textValueSchema)}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.options`}
          component={InputField}
          label={t('pages.campaign.checkboxField.options')}
          multiline
          maxRows={5}
          validate={(value: string) => validate(value, optionsSchema)}
          helperText={t('pages.campaign.checkboxField.optionsHelper')}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.color`}
          component={ColorPickerField}
          label={t('pages.campaign.checkboxField.color')}
          validate={(value: string) => validate(value, hexColorValidationSchema)}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.fontSize`}
          component={InputField}
          label={t('pages.campaign.checkboxField.fontSize')}
          type="number"
          validate={(value: string) => validate(value, fontSizeValueSchema)}
        />
      </FormRow>

      <FormRow>
        <Field name={`${name}.fontWeight`} component={InputField} label={t('pages.campaign.checkboxField.fontWeight')} select defaultValue="400">
          {[300, 400, 500, 700].map((fontWeight) => (
            <MenuItem key={fontWeight} value={fontWeight}>
              {fontWeight}
            </MenuItem>
          ))}
        </Field>
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.labelFontSize`}
          component={InputField}
          label={t('pages.campaign.checkboxField.labelFontSize')}
          type="number"
          validate={(value: string) => validate(value, fontSizeValueSchema)}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.labelFontWeight`}
          component={InputField}
          label={t('pages.campaign.checkboxField.labelFontWeight')}
          select
          defaultValue="400"
        >
          {[300, 400, 500, 700].map((fontWeight) => (
            <MenuItem key={fontWeight} value={fontWeight}>
              {fontWeight}
            </MenuItem>
          ))}
        </Field>
      </FormRow>

      <FormRow>
        <Field name={`${name}.row`} component={CheckboxField} label={t('pages.campaign.checkboxField.inRow')} />
      </FormRow>

      <FormRow>
        <Field name={`${name}.required`} component={CheckboxField} label={t('pages.campaign.checkboxField.required')} />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.name`}
          component={InputField}
          label={t('pages.campaign.checkboxField.name')}
          validate={(value: string) => validate(value, textValueSchema)}
          helperText={t('pages.campaign.checkboxField.nameInfo')}
        />
      </FormRow>

      {remove && (
        <Button color="error" onClick={remove}>
          {t('pages.campaign.checkboxField.buttonRemove')}
        </Button>
      )}
    </RadioGroupFieldSettingsWrapper>
  );
};

type RadioGroupFieldProps = {
  name: string;
  remove?: () => void;
};

export const RadioGroupField: FC<RadioGroupFieldProps> = ({ name, remove }) => {
  const [field] = useField<RadioGroup>(name);
  const dispatch = useAppDispatch();

  const {
    value: { defaultValue, label, fontWeight, color, row, fontSize, labelFontSize, labelFontWeight },
  } = field;

  const handleClick = (event: MouseEvent) => {
    event.preventDefault();

    dispatch(
      openSidebar({
        content: <RadioGroupFieldSettings name={name} remove={remove} />,
      })
    );
  };

  const options = field.value.options
    .split(',')
    .map((option) => option.trim())
    .filter(Boolean);

  return (
    <FormBlock onClick={handleClick} style={{ marginBottom: '-6px' }}>
      <FormControl>
        <FormLabel>
          <span style={{ color, fontWeight: labelFontWeight, fontSize: `${labelFontSize}px` }}>{label}</span>
        </FormLabel>

        <MuiRadioGroup value={defaultValue.trim()} row={row}>
          {options.map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              control={<Radio size="small" style={{ color }} />}
              label={<span style={{ color, fontWeight, fontSize: `${fontSize}px` }}>{option}</span>}
            />
          ))}
        </MuiRadioGroup>
      </FormControl>

      <Cover />
    </FormBlock>
  );
};

const RadioGroupFieldSettingsWrapper = styled.div``;

const Cover = styled.div`
  position: absolute;
  z-index: 1000;
  inset: 0 0 0 0;
  cursor: pointer;
  pointer-events: none;
`;
